import {
  GET_SSH_HOST_KEYS_DOCUMENT_REFERENCE,
  GOVCLOUD_REGIONS,
  PROVISION_USER_ACCESS_DOCUMENT_REFERENCE,
} from "@p0security/integrations-aws-shared/constants";
import { awsPartitionFromUse } from "@p0security/integrations-aws-shared/gov-cloud";
import type { AwsIntegration } from "@p0security/integrations-aws-shared/types";
import { AwsPartition } from "@p0security/integrations-aws-shared/util";
import { isAwsId } from "@p0security/integrations-ssh-shared/util";
import { generateSsmDocumentYaml } from "@p0security/shared/integrations/resources/ssh/ssmDocumentYaml";
import { assertNeverError } from "@p0security/shared/types/index";
import { Typography } from "@p0security/ui";
import { pick } from "lodash";

import { sshCspKeys } from "../constants.ts";
import { SudoSshInstaller } from "../types";

const GET_SSH_HOST_KEYS_DOCUMENT = (
  bashEscape: string,
  tfEscape: string
) => `schemaVersion: "2.2"
description: "Retrieve machine registration information including SSH host keys"
mainSteps:
- precondition:
    StringEquals:
      - platformType
      - Linux
  action: aws:runShellScript
  name: InvokeLinuxScript
  inputs:
    runCommand:
      - |
        #!/bin/bash
        set -e

        GetMachineHostKeys() {
          local keys=()
          for key_type in rsa dsa ecdsa ed25519; do
            path="/etc/ssh/ssh_host_${tfEscape}${bashEscape}\${key_type}_key.pub"
            [[ -f "${bashEscape}\$path" ]] && keys+=("${bashEscape}\$(<"${bashEscape}\$path")")
          done
          printf '{"hostKeys":['
          for i in "${tfEscape}${bashEscape}\${!keys[@]}"; do
            [[ ${bashEscape}\$i -gt 0 ]] && printf ','
            printf '"%s"' "${tfEscape}${bashEscape}\${keys[${bashEscape}\$i]}"
          done
          printf ']}'
        }

        GetMachineHostKeys
`;

const regionsInCommercialAws = `export REGIONS_ALL=$( \\
  aws account list-regions --output text \\
  --query 'Regions[?(RegionOptStatus!=\`DISABLED\` && RegionOptStatus!=\`DISABLING\`)].RegionName' \\
)`;

const regionsInGovCloudAws = `export REGIONS_ALL="${GOVCLOUD_REGIONS.join(" ")}"`;

const allRegionsScript = (awsPartition: AwsPartition) => {
  if (awsPartition === "aws") {
    return regionsInCommercialAws;
  } else if (awsPartition === "aws-us-gov") {
    return regionsInGovCloudAws;
  }
  throw assertNeverError(awsPartition);
};

const sudoSshCliFor = (awsPartition: AwsPartition) => {
  const userAccessDocumentName =
    PROVISION_USER_ACCESS_DOCUMENT_REFERENCE.documentName;
  const hostKeysDocumentName =
    GET_SSH_HOST_KEYS_DOCUMENT_REFERENCE.documentName;
  return `cat << EOF>$HOME/${userAccessDocumentName}.yaml
${generateSsmDocumentYaml("bash")}
EOF
cat << EOF>$HOME/${hostKeysDocumentName}.yaml
${GET_SSH_HOST_KEYS_DOCUMENT("\\", "")}
EOF
${allRegionsScript(awsPartition)}
for region in $REGIONS_ALL; do 
  # Create/update provision user access document
  if aws ssm describe-document --name "${userAccessDocumentName}" --region $region > /dev/null 2>&1; then
      aws ssm delete-document --name "${userAccessDocumentName}" --region $region
  fi
  aws ssm create-document --name "${userAccessDocumentName}" --document-type "Command" --document-format "YAML" --target-type "/AWS::EC2::Instance" --tags Key=used-by,Value=P0Security --content file://$HOME/${userAccessDocumentName}.yaml --region $region
  
  # Create/update machine registration document
  if aws ssm describe-document --name "${hostKeysDocumentName}" --region $region > /dev/null 2>&1; then
      aws ssm delete-document --name "${hostKeysDocumentName}" --region $region
  fi
  aws ssm create-document --name "${hostKeysDocumentName}" --document-type "Command" --document-format "YAML" --target-type "/AWS::EC2::Instance" --tags Key=used-by,Value=P0Security --content file://$HOME/${hostKeysDocumentName}.yaml --region $region
done
  `;
};

const sudoSshIacFor = () => {
  return `
resource "aws_ssm_document" "p0_manage_sudo_access" {
  name            = "${PROVISION_USER_ACCESS_DOCUMENT_REFERENCE.documentName}"
  document_format = "YAML"
  document_type   = "Command"
  target_type     = "/AWS::EC2::Instance"

  content = <<DOC
${generateSsmDocumentYaml("terraform")}
DOC
  tags = {
    managed-by = "terraform"
    used-by    = "P0Security"
  }
}

resource "aws_ssm_document" "p0_get_ssh_host_keys" {
  name            = "${GET_SSH_HOST_KEYS_DOCUMENT_REFERENCE.documentName}"
  document_format = "YAML"
  document_type   = "Command"
  target_type     = "/AWS::EC2::Instance"

  content = <<DOC
${GET_SSH_HOST_KEYS_DOCUMENT("", "$")}
DOC
  tags = {
    managed-by = "terraform"
    used-by    = "P0Security"
  }
}`;
};

const sudoSshInstructions: SudoSshInstaller["instructions"] = (context, id) => {
  const cspConfigs = pick(context.installed, sshCspKeys);
  if (isAwsId(id)) {
    // @ts-expect-error intentional type narrowing
    const aws: AwsIntegration | undefined = cspConfigs.aws;
    if (!aws) return {};
    const [_, accountId] = id.split(":");
    const awsPartition = awsPartitionFromUse(aws, accountId, "iam-write");
    return {
      help: (
        <Typography.Paragraph>
          Create SSM command document for P0 to provision access to the
          instance. P0 requires the document to be created in all regions for
          the account.
        </Typography.Paragraph>
      ),
      commands: {
        shell: [{ command: sudoSshCliFor(awsPartition) }],
        iac: [{ command: sudoSshIacFor() }],
      },
    };
  } else {
    return {
      help: (
        <Typography.Paragraph>Click next to continue.</Typography.Paragraph>
      ),
    };
  }
};

export const sudoSshInstaller: SudoSshInstaller = {
  instructions: sudoSshInstructions,
};
